import axios from "axios";
import React, { useEffect, useRef, useState, useContext } from "react";
import PCModal from "../modal/PC/PCModal";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import MicIcon from "@mui/icons-material/Mic";
import StopIcon from "@mui/icons-material/Stop";
import HomeIcon from "@mui/icons-material/Home";
import SendIcon from "@mui/icons-material/Send";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import Tooltip from "@mui/material/Tooltip";
import Swal from "sweetalert2";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { AppContext } from '../AppStore';
import OrderRegModal from "../modal/PC/order/OrderRegModal";
import OutgoingRegModal from "../modal/PC/order/OutgoingRegModal";
import PurchaseRegModal from "../modal/PC/purchase/PurchaseRegModal";
import IncomingRegModal from "../modal/PC/purchase/IncomingRegModal";
/**
 * ✅ (통합 확정) 요구사항 반영
 * - Android WebView: 네이티브 STT/TTS 브릿지(window.Android.*)
 * - iPhone/PC Web: Web Speech API (SpeechRecognition / speechSynthesis)
 * - TTS 재생 중 STT 금지
 * - 입력이 비어있으면 STT 자동 대기(웹/앱 포함)
 * - "말하고 난 후 입력폼 반영 → 잠깐 대기(변경 없으면) → 자동 전송"
 * - 마이크로 전송한 질문이면 답변 자동 TTS(웹/앱 포함)
 * - PC 모달에서 입력영역이 항상 맨 아래 오도록(모달만) 레이아웃 고정
 * - 위치: React에서 사용할 수 있게 저장만(geoRef.current). 필요 시 payload에 붙여 사용.
 * - ✅ 추가: 상단 제목 오른쪽에 "시/군"까지만 표시 (예: 평택시)
 * - 꾹 누르면(롱프레스) 상세주소 툴팁 노출
 */

/**
 * ✅ 코드/번호 TTS 매핑 (확정본)
 * 생산작업번호 : W / 분실번호 : L / 폐기번호 : D / 재고조정번호 : S /
 * 발주번호 : P / 입고번호 : I / 수주번호 : O / 출하번호 : R /
 * 자원번호 : M / 품질측정번호 : Q / 프로젝트 번호 : F
 */
const CODE_PREFIX = {
  W: { label: "생산작업번호" },
  L: { label: "분실번호" },
  D: { label: "폐기번호" },
  S: { label: "재고조정번호" },
  P: { label: "발주번호" },
  I: { label: "입고번호" },
  O: { label: "수주번호" },
  R: { label: "출하번호" },
  M: { label: "자원번호" },
  Q: { label: "품질측정번호" },
  F: { label: "프로젝트번호" },
};

// ✅ 채팅 유지(마이크/스피커/사파리에서 리마운트돼도 안날아가게)
// ✅ 위치 라벨 캐시 키(시/군)
const buildGeoLabelStorageKey = () => {
  try {
    const companyId = localStorage.getItem("company_id") ?? "0";
    const path =
      typeof window !== "undefined" ? window.location.pathname : "unknown";
    return `diff_chat_geo_label_v1:${companyId}:${path}`;
  } catch (e) {
    return "diff_chat_geo_label_v1";
  }
};

// ✅ 위치 상세주소 캐시 키(display_name)
const buildGeoFullStorageKey = () => {
  try {
    const companyId = localStorage.getItem("company_id") ?? "0";
    const path =
      typeof window !== "undefined" ? window.location.pathname : "unknown";
    return `diff_chat_geo_full_v1:${companyId}:${path}`;
  } catch (e) {
    return "diff_chat_geo_full_v1";
  }
};

// =====================
// ✅ Android 브릿지 유틸
// =====================
const isAndroidWebView = () => {
  try {
    return typeof window !== "undefined" && !!window.Android;
  } catch {
    return false;
  }
};

const androidCall = (fn, ...args) => {
  try {
    const a = window.Android;
    if (!a || typeof a[fn] !== "function") return false;
    a[fn](...args);
    return true;
  } catch {
    return false;
  }
};

// =====================
// ✅ Web TTS 지원 체크
// =====================
const hasWebTTS =
  typeof window !== "undefined" &&
  typeof window.speechSynthesis !== "undefined" &&
  typeof window.SpeechSynthesisUtterance !== "undefined";

// =====================
// ✅ Reverse Geocode (시/군까지만 + 상세주소)
// - 무료 OSM Nominatim 사용
// - 상용 대량 트래픽이면 카카오/네이버로 교체 권장
// =====================

const pickCityOrCounty = (addr = {}) => {
  return (
    addr.city ||
    addr.municipality ||
    addr.city_district ||
    addr.borough ||
    addr.town ||
    addr.village ||
    addr.suburb ||
    addr.county ||
    addr.state ||
    ""
  );
};

const trimToSiGun = (name) => {
  const t = String(name || "").trim();
  if (!t) return "";

  const first = t.split(/\s+/)[0];

  return first;
};


// ✅ 시/군 라벨 축약: 서울특별시→서울시, 부산광역시→부산시, 제주특별자치시→제주시 등
const shortenSiGunLabel = (label) => {
  let t = String(label || "").trim();
  if (!t) return "";
  // 공백 있으면 첫 토큰만
  t = t.split(/\s+/)[0];
  t = t
    .replace(/특별자치시$/g, "시")
    .replace(/특별시$/g, "시")
    .replace(/광역시$/g, "시")
    .replace(/특별자치도$/g, "도");
  return t;
};

const geoLabelBoxStyle = {
  display: "flex",
  alignItems: "center",
  gap: 4,
  padding: "4px 10px",
  border: "none",
  borderRadius: 8,
  backgroundColor: "transparent",
  minWidth: 90,
};
const geoLabelStyle = {
  fontSize: 14,
  color: "#6b7280",
  fontWeight: 700,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  textAlign: "right",
  minWidth: 0,
};
const locationIconStyle = {
  width: 32,
  height: 32,
  padding: "2px",
  border: "none",
  borderRadius: "8px",
  backgroundColor: "#fff",
  color: "#6b7280",
  "&:hover": { backgroundColor: "#f3f4f6" },
};
const closeButtonStyle = {
  width: 34,
  height: 34,
  padding: "4px",
  borderRadius: 2,
  border: "1px solid #e5e7eb",
  backgroundColor: "#fff",
  "&:hover": { backgroundColor: "#f3f4f6" },
};

// ✅ Nominatim 주소를 한국식 순서로 최대한 정리
const formatKoreanAddressFromNominatim = (addr = {}) => {
  const pick = (v) => String(v || "").trim();

  // 시/군(라벨)
  const siGunRaw = trimToSiGun(pickCityOrCounty(addr) || addr.state || "");
  const siGun = shortenSiGunLabel(siGunRaw);

  // 구/군
  const maybeCounty = pick(addr.county);
  const guGun =
    pick(addr.city_district) ||
    pick(addr.borough) ||
    ((/([가-힣]+(구|군))$/.test(maybeCounty)) ? maybeCounty : "");

  // 동/읍/면/리
  const dong =
    pick(addr.suburb) ||
    pick(addr.neighbourhood) ||
    pick(addr.quarter) ||
    pick(addr.town) ||
    pick(addr.village) ||
    "";

  // 도로명 + 번지
  const road = pick(addr.road) || pick(addr.residential) || pick(addr.pedestrian) || "";
  const house = pick(addr.house_number);
  const detail = [road, house].filter(Boolean).join(" ").trim();

  const parts = [siGun, guGun, dong, detail].filter(Boolean);

  // 중복 제거(연속 중복)
  const uniq = [];
  for (const part of parts) {
    if (!uniq.length || uniq[uniq.length - 1] !== part) uniq.push(part);
  }
  return uniq.join(" ").trim();
};

const reverseGeocodeToGeoInfo = async (lat, lng) => {
  try {
    const la = Number(lat);
    const lo = Number(lng);
    if (!Number.isFinite(la) || !Number.isFinite(lo)) return { label: "", full: "" };

    // Nominatim reverse
    const url =
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2` +
      `&lat=${encodeURIComponent(la)}` +
      `&lon=${encodeURIComponent(lo)}` +
      `&accept-language=ko`;

    const res = await fetch(url, {
      method: "GET",
      headers: {},
    });

    if (!res.ok) return { label: "", full: "" };
    const data = await res.json();

    const addr = data?.address || {};
    const rawLabel = trimToSiGun(pickCityOrCounty(addr) || addr.state || "");

    // ✅ 라벨: '서울특별시' → '서울시' 처럼 축약
    const label = shortenSiGunLabel(rawLabel) || "";

    // ✅ 상세주소: 시/군/구/동 + 도로명/번지 순서로 구성
    const full =
      formatKoreanAddressFromNominatim(addr) ||
      String(data?.display_name || "").trim();

    return { label, full };

  } catch (e) {
    return { label: "", full: "" };
  }
};

function ChatModal({
  chatModalClose,
  fullScreen = false,
  showSidebarButton = false,
  onSidebarClick,
}) {
  const appStore = useContext(AppContext);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const handleSendTextRef = useRef(null);

  // ✅ PC 첨부(엑셀 업로드)
  const fileInputRef = useRef(null);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [attachMenuAnchor, setAttachMenuAnchor] = useState(null);
  const attachMenuOpen = Boolean(attachMenuAnchor);

  // ✅ 드래그앤드랍 업로드
  const [isDragOver, setIsDragOver] = useState(false);
  const dragCounterRef = useRef(0);

  // ✅ DataGrid 드롭다운을 위한 공통코드 상태 추가
  const [userTypeModalOption, setUserTypeModalOption] = useState([]);
  const [unitCurrencyModalOption, setUnitCurrencyModalOption] = useState([]);
  const [qualityYnModalOption, setQualityYnModalOption] = useState([
    { value: 'Y', label: 'Y' },
    { value: 'N', label: 'N' }
  ]);

  // =====================
  // ✅ 등록 모달 상태
  // =====================
  // 컴포넌트 안쪽 (orderRegOpen 근처)
  const [orderRegOpen, setOrderRegOpen] = useState(false);
  const [orderRegPayload, setOrderRegPayload] = useState(null);
  const [orderMenuUpdateYN, setOrderMenuUpdateYN] = useState('N');
  
  const [purchaseRegOpen, setPurchaseRegOpen] = useState(false);
  const [purchaseRegPayload, setPurchaseRegPayload] = useState(null);
  const [purchaseMenuUpdateYN, setPurchaseMenuUpdateYN] = useState('N');

  const [incomingRegOpen, setIncomingRegOpen] = useState(false);
  const [incomingRegPayload, setIncomingRegPayload] = useState(null);
  const [incomingMenuUpdateYN, setIncomingMenuUpdateYN] = useState('N');

  const [outgoingRegOpen, setOutgoingRegOpen] = useState(false);
  const [outgoingRegPayload, setOutgoingRegPayload] = useState(null);
  const [outgoingMenuUpdateYN, setOutgoingMenuUpdateYN] = useState('N');

  // ✅ 챗봇 창 열릴 때 각 화면 권한 체크
  useEffect(() => {
    try {
      const accessList = JSON.parse(localStorage.getItem("nav"));
      setOrderMenuUpdateYN(accessList.find(item => String(item.menu_url) === String("/p/order").split("/").slice(0, 3).join("/")).menu_update_yn);
      setPurchaseMenuUpdateYN(accessList.find(item => String(item.menu_url) === String("/p/purchase").split("/").slice(0, 3).join("/"))?.menu_update_yn);
      setIncomingMenuUpdateYN(accessList.find(item => String(item.menu_url) === String("/p/incoming").split("/").slice(0, 3).join("/"))?.menu_update_yn || 'N');
      setOutgoingMenuUpdateYN(accessList.find(item => String(item.menu_url) === String("/p/outgoing").split("/").slice(0, 3).join("/"))?.menu_update_yn || 'N');
    } catch (e) {
      console.error("권한 에러:", e);
    }
  }, []);

  // ✅ 공통코드 조회
  useEffect(() => {
    axios.get('/api/Common/CodeDetailList')
      .then((response) => {
        var user_type_array_modal = [];
        var unit_currency_array_modal = [];
        for(let i=0; i<(response.data).length; i++){
          if((response.data)[i].code_id === '0017'){ // 통화단위
            unit_currency_array_modal.push({ value: (response.data)[i].code_detail_seq, label: (response.data)[i].code_detail_name });
          } else if((response.data)[i].code_id === '0020'){ // 담당자유형
            user_type_array_modal.push({ value: (response.data)[i].code_detail_seq, label: (response.data)[i].code_detail_name });
          }
        }
        setUnitCurrencyModalOption(unit_currency_array_modal);
        setUserTypeModalOption(user_type_array_modal);
      })
      .catch((error) => {
        console.log("공통코드 조회 에러:", error);
      });
  }, []);

  const openOrderReg = (payload = null) => {
    setOrderRegPayload(payload);
    setOrderRegOpen(true);
  };

  // ===== 설정 =====
  const closeOrderReg = () => {
    setOrderRegOpen(false);
    setOrderRegPayload(null);
  };

  // ✅ [추가] 수주 등록 성공 시 호출될 함수
  const handleRegSuccessSave = (selectID) => {
    closeOrderReg(); // 등록 모달 닫기
    
    Swal.fire({
      title: '수주 등록 완료',
      text: '상세 내역을 확인하시겠습니까?',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: '상세',
      cancelButtonText: '닫기'
    }).then((result) => {
      if (result.isConfirmed) {
        // ✅ 새 탭으로 수주 관리 페이지 열기 (파라미터로 selectID 전달)
        window.open(`/p/order/${selectID}`, '_blank');
      }
    });
  };

  const openPurchaseReg = (payload = null) => {
    setPurchaseRegPayload(payload);
    setPurchaseRegOpen(true);
  };
  const closePurchaseReg = () => {
    setPurchaseRegOpen(false);
    setPurchaseRegPayload(null);
  };
  const handlePurchaseRegSuccessSave = (selectID) => {
    closePurchaseReg();
    Swal.fire({
      title: '발주 등록 완료',
      text: '상세 내역을 확인하시겠습니까?',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: '상세',
      cancelButtonText: '닫기'
    }).then((result) => {
      if (result.isConfirmed) {
        window.open(`/p/purchase/${selectID}`, '_blank');
      }
    });
  };

  const openIncomingReg = (payload = null) => {
    setIncomingRegPayload(payload);
    setIncomingRegOpen(true);
  };
  const closeIncomingReg = () => {
    setIncomingRegOpen(false);
    setIncomingRegPayload(null);
  };
  const handleIncomingRegSuccessSave = (selectID) => {
    closeIncomingReg();
    Swal.fire({
      title: '입고 등록 완료',
      text: '상세 내역을 확인하시겠습니까?',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: '상세',
      cancelButtonText: '닫기'
    }).then((result) => {
      if (result.isConfirmed) {
        window.open(`/p/incoming/${selectID}`, '_blank');
      }
    });
  };

  const openOutgoingReg = (payload = null) => {
    setOutgoingRegPayload(payload);
    setOutgoingRegOpen(true);
  };
  const closeOutgoingReg = () => {
    setOutgoingRegOpen(false);
    setOutgoingRegPayload(null);
  };
  const handleOutgoingRegSuccessSave = (selectID) => {
    closeOutgoingReg();
    Swal.fire({
      title: '출하 등록 완료',
      text: '상세 내역을 확인하시겠습니까?',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: '상세',
      cancelButtonText: '닫기'
    }).then((result) => {
      if (result.isConfirmed) {
        window.open(`/p/outgoing/${selectID}`, '_blank');
      }
    });
  };

  // ===== 설정 =====
  const HISTORY_MAX = 20;
  const DAILY_LIMIT = 10;

  const isLocalhost =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1");
  const API_URL = isLocalhost ? "http://127.0.0.1:8585/chat" : "/chat";

  const isMobileChat =
    typeof window !== "undefined" &&
    window.location.pathname.startsWith("/m/chat");

  // ✅ 정책(요청): 모바일(/m/chat)에서만 마이크, PC에서는 숨김(=첨부만)
  const enableMic = isMobileChat;

  // ✅ 모바일(/m/chat)은 사실상 fullScreen처럼 동작해야 안정적
  const shouldFull = fullScreen || isMobileChat;

  // ✅ 상단 헤더 높이(페이지 고정 헤더 포함) 자동 보정
  const [topOffset, setTopOffset] = useState(0);

  // ✅ 상단 제목 오른쪽에 표시할 위치 라벨 (예: 평택시)
  const [geoLabel, setGeoLabel] = useState(() => {
    try {
      if (typeof window === "undefined") return "";
      const key = buildGeoLabelStorageKey();
      return sessionStorage.getItem(key) || "";
    } catch (e) {
      return "";
    }
  });

  // ✅ 롱프레스 툴팁에 표시할 상세주소
  const [geoFull, setGeoFull] = useState(() => {
    try {
      if (typeof window === "undefined") return "";
      const key = buildGeoFullStorageKey();
      return sessionStorage.getItem(key) || "";
    } catch (e) {
      return "";
    }
  });

  // ✅ 주소 툴팁(아이콘 클릭) 5초 후 자동 닫힘
  const [geoTipOpen, setGeoTipOpen] = useState(false);
  const geoTipTimerRef = useRef(null);

  const closeGeoTip = () => {
    setGeoTipOpen(false);
    if (geoTipTimerRef.current) {
      clearTimeout(geoTipTimerRef.current);
      geoTipTimerRef.current = null;
    }
  };

  const openGeoTip = () => {
    if (!geoFull) return;
    setGeoTipOpen(true);
    if (geoTipTimerRef.current) clearTimeout(geoTipTimerRef.current);
    geoTipTimerRef.current = setTimeout(() => {
      setGeoTipOpen(false);
      geoTipTimerRef.current = null;
    }, 5000);
  };
  // =========================================================
  // ✅ (핵심) iOS/Android WebView 뷰포트 높이 변수 + 상단 헤더 높이 감지
  // =========================================================
  useEffect(() => {
    if (typeof window === "undefined") return;

    const setAppHeight = () => {
      const h = window.visualViewport?.height || window.innerHeight || 0;
      document.documentElement.style.setProperty("--app-height", `${h}px`);
    };

    const measureTopHeaders = () => {
      try {
        const selectors = [
          "header",
          "#header",
          "#appHeader",
          ".app-header",
          ".MuiAppBar-root",
          ".navbar",
          ".topbar",
          ".header",
          "[data-fixed-header='true']",
        ];

        const candidates = [];
        selectors.forEach((sel) => {
          document.querySelectorAll(sel).forEach((el) => candidates.push(el));
        });

        const uniq = Array.from(new Set(candidates));
        let sum = 0;

        for (const el of uniq) {
          if (!el) continue;

          const style = window.getComputedStyle(el);
          const pos = style.position;
          if (pos !== "fixed" && pos !== "sticky") continue;

          const rect = el.getBoundingClientRect();
          if (rect.height <= 0) continue;
          if (rect.bottom <= 0) continue;
          if (rect.top > 4) continue;
          if (rect.width < window.innerWidth * 0.6) continue;

          sum += rect.height;
        }

        setTopOffset(Math.round(sum));
      } catch (e) {
        setTopOffset(0);
      }
    };

    const tick = () => {
      setAppHeight();
      measureTopHeaders();
    };

    tick();

    window.addEventListener("resize", tick);
    window.addEventListener("orientationchange", tick);
    window.visualViewport?.addEventListener("resize", tick);
    window.visualViewport?.addEventListener("scroll", tick);

    const t1 = setTimeout(tick, 200);
    const t2 = setTimeout(tick, 800);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener("resize", tick);
      window.removeEventListener("orientationchange", tick);
      window.visualViewport?.removeEventListener("resize", tick);
      window.visualViewport?.removeEventListener("scroll", tick);
    };
  }, []);

  // ✅ shouldFull일 때 바깥(body/html) 스크롤 차단
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!shouldFull) return;

    const html = document.documentElement;
    const body = document.body;

    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    const prevBodyHeight = body.style.height;

    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    body.style.height = "100%";

    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      body.style.height = prevBodyHeight;
    };
  }, [shouldFull]);

  // ✅ messages 초기값: sessionStorage 복원 (없으면 인사말)
  const [messages, setMessages] = useState(() => [
    {
      role: "assistant",
      content:
        "안녕하세요! " +
        (localStorage.getItem("company_nm") ?? "") +
        " MES 관련해서 무엇이든 물어보세요 :)",
      ts: Date.now(),
      attachments: [],
    },
  ]);

  const [input, setInput] = useState("");
  const inputStateRef = useRef("");
  useEffect(() => {
    inputStateRef.current = input;
  }, [input]);

  const [isLoading, setIsLoading] = useState(false);

  // ✅ 콜백(특히 Android bridge)에서 최신 상태를 보게 하기 위한 Ref
  const isLoadingRef = useRef(false);
  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  // ===== 위치 저장 (React에서 쓸 수 있게만) =====
  // geoRef.current = { lat, lng, accuracy, ts, from }
  const geoRef = useRef(null);

  // ===== STT =====
  const [isListening, setIsListening] = useState(false);
  const isListeningRef = useRef(false);
  useEffect(() => {
    isListeningRef.current = isListening;
  }, [isListening]);
  const [sttDraft, setSttDraft] = useState(""); // 웹 STT interim 표시용(기존 UI 유지)
  const recognitionRef = useRef(null);
  const sttFinalRef = useRef("");
  const sttTimerRef = useRef(null);

  const sttDraftRef = useRef("");
  useEffect(() => {
    sttDraftRef.current = sttDraft;
  }, [sttDraft]);

  const sttSilenceRef = useRef(null);
  const sttAutoSendRef = useRef(false);

  const STT_SILENCE_MS = 1800;

  const sttHasSpeechRef = useRef(false);
  const sttBaseInputRef = useRef("");

  const sttUsedRef = useRef(false);
  const lastSendByMicRef = useRef(false);

  // ✅ “한 번 시작(버튼 클릭)” 이후, 입력이 비어있으면 자동 대기 유지
  const autoListenRef = useRef(false);

  // ✅ stop 후 전송 딜레이
  const STT_SEND_DELAY_MS = 1000;

  // ✅ "입력폼 반영 후 변경 없으면 자동전송"용 디바운스
  const pendingAutoSendTimerRef = useRef(null);
  const pendingAutoSendTextRef = useRef("");
  const cancelPendingAutoSend = () => {
    if (pendingAutoSendTimerRef.current) {
      clearTimeout(pendingAutoSendTimerRef.current);
      pendingAutoSendTimerRef.current = null;
    }
    pendingAutoSendTextRef.current = "";
  };
  useEffect(() => {
    return () => cancelPendingAutoSend();
  }, []);

  // ===== TTS =====
  const [isSpeaking, setIsSpeaking] = useState(false);
  const isSpeakingRef = useRef(false);
  useEffect(() => {
    isSpeakingRef.current = isSpeaking;
  }, [isSpeaking]);
  const [speakingIdx, setSpeakingIdx] = useState(null);
  const utterRef = useRef(null);

  const ttsQueueRef = useRef([]);
  const ttsQueueIdxRef = useRef(0);

  const [stickyLeft, setStickyLeft] = useState(0);
  const [askCount, setAskCount] = useState(0);
  const [locked, setLocked] = useState(false);
  const lockedRef = useRef(false);
  useEffect(() => {
    lockedRef.current = locked;
  }, [locked]);

  useEffect(() => {
    if (!sttUsedRef.current) return;
    cancelPendingAutoSend();
    const candidate = input.trim();
    if (!candidate) return;
    if (locked || isLoading || isListening) return;
    pendingAutoSendTextRef.current = candidate;
    pendingAutoSendTimerRef.current = setTimeout(() => {
      pendingAutoSendTimerRef.current = null;
      const pendingText = pendingAutoSendTextRef.current;
      pendingAutoSendTextRef.current = "";
      if (!pendingText) return;
      handleSendTextRef.current?.(pendingText, false);
    }, 1000);
  }, [input, locked, isLoading, isListening]);

  const [lastSqlKey, setLastSqlKey] = useState(null);
  const [lastToolParams, setLastToolParams] = useState({});
  const [lastToolRowsPreview, setLastToolRowsPreview] = useState([]);

  useEffect(() => {
    setAskCount(0);
    setLocked(false);
  }, []);

  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, isLoading]);

  useEffect(() => {
    const t = setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
    return () => clearTimeout(t);
  }, []);

  // ✅ 웹 위치 1회 저장
  const requestWebGeoOnce = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        geoRef.current = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          ts: Date.now(),
          from: "web",
        };

        // ✅ 시/군 라벨 + 상세주소 업데이트
        const info = await reverseGeocodeToGeoInfo(
          geoRef.current.lat,
          geoRef.current.lng
        );
        if (info?.label) {
          setGeoLabel(info.label);
          try {
            const key = buildGeoLabelStorageKey();
            sessionStorage.setItem(key, info.label);
          } catch (e) {}
        }

        if (info?.full) {
          setGeoFull(info.full);
          try {
            const key = buildGeoFullStorageKey();
            sessionStorage.setItem(key, info.full);
          } catch (e) {}
        }
      },
      () => {},
      { enableHighAccuracy: true, maximumAge: 15000, timeout: 12000 }
    );
  };

  // ✅ Android 위치 1회 저장
  const requestAndroidGeoOnce = () => {
    const rid = `geo_${Date.now()}`;
    const ok = androidCall("getLocationOnce", rid);
    if (!ok) requestWebGeoOnce();
  };

  // ✅ 위치 1회 요청(자동)
  const requestGeoOnce = () => {
    if (isAndroidWebView()) requestAndroidGeoOnce();
    else requestWebGeoOnce();
  };

  // ✅ 최초 1회 위치 요청 (이미 캐시가 있으면 굳이 또 안함)
  useEffect(() => {
    if (typeof window === "undefined") return;

    try {
      const kLabel = buildGeoLabelStorageKey();
      const kFull = buildGeoFullStorageKey();
      const cachedLabel = sessionStorage.getItem(kLabel) || "";
      const cachedFull = sessionStorage.getItem(kFull) || "";
      if (cachedLabel) setGeoLabel(cachedLabel);
      if (cachedFull) setGeoFull(cachedFull);
      if (cachedLabel) return; // 라벨이 있으면 굳이 다시 조회하지 않음
    } catch (e) {}

    // 너무 즉시 요청하면 UX가 싫을 수 있어 살짝 딜레이
    const t = setTimeout(() => {
      requestGeoOnce();
    }, 600);

    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const pollId = setInterval(() => {
      requestGeoOnce();
    }, 60000);
    return () => clearInterval(pollId);
  }, []);

  // ✅ Android 브릿지 콜백 바인딩 (STT/TTS/위치)
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!isAndroidWebView()) return;

    window.__DIFF_CHAT_STT_RESULT__ = (text) => {
      const t = String(text || "").trim();
      setIsListening(false);
      if (!t) return;

      sttUsedRef.current = true;
      lastSendByMicRef.current = true;
      setInput(t);

      // 디바운스(변경 없으면 자동 전송)
      pendingAutoSendTextRef.current = t;
      if (pendingAutoSendTimerRef.current) {
        clearTimeout(pendingAutoSendTimerRef.current);
        pendingAutoSendTimerRef.current = null;
      }

      pendingAutoSendTimerRef.current = setTimeout(() => {
        const cur = String(inputStateRef.current || "").trim();
        const target = String(pendingAutoSendTextRef.current || "").trim();
        if (cur && target && cur === target && !isLoadingRef.current && !lockedRef.current) {
          handleSendTextRef.current?.(cur, true);
        }
      }, STT_SEND_DELAY_MS);
    };

    window.__DIFF_CHAT_STT_ERROR__ = () => {
      setIsListening(false);
    };

    window.__DIFF_CHAT_TTS_START__ = () => {
      setIsSpeaking(true);
      isSpeakingRef.current = true;
      if (isListeningRef.current) {
        try {
          androidCall("stopSTT");
        } catch (e) {}
        setIsListening(false);
      }
    };

    window.__DIFF_CHAT_TTS_END__ = () => {
      setIsSpeaking(false);
      isSpeakingRef.current = false;
      setSpeakingIdx(null);
    };

    window.__DIFF_CHAT_GEO_RESULT__ = async (obj) => {
      try {
        geoRef.current = {
          lat: Number(obj?.lat),
          lng: Number(obj?.lng),
          accuracy: Number(obj?.accuracy),
          ts: Number(obj?.ts) || Date.now(),
          from: "android",
        };

        const info = await reverseGeocodeToGeoInfo(geoRef.current.lat, geoRef.current.lng);
        if (info?.label) {
          setGeoLabel(info.label);
          try {
            const key = buildGeoLabelStorageKey();
            sessionStorage.setItem(key, info.label);
          } catch (e) {}
        }

        if (info?.full) {
          setGeoFull(info.full);
          try {
            const key = buildGeoFullStorageKey();
            sessionStorage.setItem(key, info.full);
          } catch (e) {}
        }
      } catch (e) {}
    };

    return () => {
      try {
        delete window.__DIFF_CHAT_STT_RESULT__;
        delete window.__DIFF_CHAT_STT_ERROR__;
        delete window.__DIFF_CHAT_TTS_START__;
        delete window.__DIFF_CHAT_TTS_END__;
        delete window.__DIFF_CHAT_GEO_RESULT__;
      } catch (e) {}
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!window.speechSynthesis) return;

    let unlocked = false;

    const unlock = () => {
      if (unlocked) return;
      unlocked = true;
      try {
        window.speechSynthesis.resume();
        const u = new window.SpeechSynthesisUtterance(" ");
        u.volume = 0;
        window.speechSynthesis.speak(u);
        window.speechSynthesis.cancel();
      } catch (e) {}
      window.removeEventListener("touchstart", unlock);
      window.removeEventListener("mousedown", unlock);
    };

    window.addEventListener("touchstart", unlock, { passive: true });
    window.addEventListener("mousedown", unlock);

    return () => {
      window.removeEventListener("touchstart", unlock);
      window.removeEventListener("mousedown", unlock);
    };
  }, []);

  const fmtKoreanDate = (y, m, d) => {
    const mm = String(m || "").replace(/^0/, "") || "0";
    const dd = String(d || "").replace(/^0/, "") || "0";
    return `${y}년 ${mm}월 ${dd}일`;
  };

  const digitsToKorean = (s) => {
    const map = {
      "0": "공",
      "1": "일",
      "2": "이",
      "3": "삼",
      "4": "사",
      "5": "오",
      "6": "육",
      "7": "칠",
      "8": "팔",
      "9": "구",
    };
    return String(s || "")
      .split("")
      .map((ch) => (map[ch] ? map[ch] : ch))
      .join("");
  };

  const cleanupNumberString = (value) =>
    String(value || "")
      .replace(/,/g, "")
      .trim();

  const numberToKoreanWords = (value) => {
    const integerPart = cleanupNumberString(String(value)).split(".")[0];
    const num = parseInt(integerPart, 10);
    if (Number.isNaN(num)) return digitsToKorean(value);
    if (num === 0) return "영";

    const sectionUnits = ["", "만", "억", "조", "경"];

    const readSection = (section) => {
      const digits = ["", "십", "백", "천"];
      const str = String(section);
      let sectionResult = "";
      for (let i = 0; i < str.length; i += 1) {
        const digit = Number(str[str.length - 1 - i]);
        if (!digit) continue;
        const unit = digits[i];
        if (i === 0 || digit !== 1 || !unit) {
          sectionResult = digitsToKorean(digit) + unit + sectionResult;
        } else {
          sectionResult = unit + sectionResult;
        }
      }
      return sectionResult;
    };

    let remaining = num;
    const parts = [];
    let sectionIndex = 0;
    while (remaining > 0) {
      const section = remaining % 10000;
      remaining = Math.floor(remaining / 10000);
      if (section) {
        const sectionText = readSection(section);
        parts.unshift(`${sectionText}${sectionUnits[sectionIndex] || ""}`);
      }
      sectionIndex += 1;
    }

    let result = parts.join("");
    if (!result) result = "영";

    const decimal = String(cleanupNumberString(value)).split(".")[1];
    if (decimal) {
      const decimalText = decimal
        .split("")
        .map((ch) => digitsToKorean(ch))
        .join("");
      result += `점${decimalText}`;
    }

    return result;
  };

  const spellNumberKorean = (value) => numberToKoreanWords(value);

  const spellNativeCounter = (value) => {
    const cleaned = cleanupNumberString(value);
    const num = parseInt(cleaned, 10);
    if (Number.isNaN(num)) return numberToKoreanWords(value);

    const singles = {
      0: "영",
      1: "한",
      2: "두",
      3: "세",
      4: "네",
      5: "다섯",
      6: "여섯",
      7: "일곱",
      8: "여덟",
      9: "아홉",
    };

    const specialTens = {
      10: "열",
      20: "스무",
      30: "서른",
      40: "마흔",
      50: "쉰",
      60: "예순",
      70: "일흔",
      80: "여든",
      90: "아흔",
    };

    if (specialTens[num]) return specialTens[num];
    if (num < 10) return singles[num] || numberToKoreanWords(num);
    if (num < 20) {
      const remainder = num - 10;
      return `열${singles[remainder] || numberToKoreanWords(remainder)}`;
    }

    if (num < 100) {
      const tens = Math.floor(num / 10) * 10;
      const ones = num % 10;
      const tensText = specialTens[tens] || `${numberToKoreanWords(tens)}`;
      if (!ones) return tensText;
      return `${tensText}${singles[ones] || numberToKoreanWords(ones)}`;
    }

    return numberToKoreanWords(num);
  };

  const highlightSpecMultipliers = (text) => {
    return text.replace(/(\d+(?:\.\d+)?)(?:\s*[x×]\s*(\d+(?:\.\d+)?))+/gi, (match) => {
      const pieces = match
        .split(/[x×]/gi)
        .map((piece) => piece.trim())
        .filter(Boolean);
      if (!pieces.length) return match;
      return pieces
        .map((piece, idx) => `${idx > 0 ? " 곱하기 " : ""}${spellNumberKorean(
          piece
        )}`)
        .join("");
    });
  };

  const highlightUnitNumbers = (text) => {
    const units = [
      "호기",
      "개",
      "EA",
      "대",
      "kg",
      "킬로",
      "톤",
      "t",
      "ton",
      "원",
      "달러",
      "\\$",
      "USD",
      "KRW",
      "건",
    ];
    const numberPattern = "\\d{1,3}(?:,\\d{3})*(?:\\.\\d+)?";
    const regex = new RegExp(
      `(${numberPattern})\\s*(${units.join("|")})`,
      "gi"
    );
    return text.replace(regex, (_, numberPart, unitPart) => {
      const cleanedUnit = unitPart.replace(/[^가-힣]/g, "");
      const ordinalUnits = new Set(["건"]);
      const nativeCounterUnits = new Set(["개", "대", "명"]);
      const cleanedNumber = cleanupNumberString(numberPart);
      let spelled;
      if (ordinalUnits.has(cleanedUnit)) {
        spelled = spellNativeCounter(cleanedNumber);
      } else if (nativeCounterUnits.has(cleanedUnit)) {
        spelled = spellNativeCounter(cleanedNumber);
      } else {
        spelled = numberToKoreanWords(cleanedNumber);
      }
      return `${spelled} ${unitPart}`;
    });
  };

  const normalizeSemantic = (s) => {
    let t = String(s ?? "");
    t = t.replace(
      /\bAPI\b(?=.*(지표|성과|현황|달성|목표|분석|관리|기준))/g,
      "KPI"
    );
    t = t.replace(/\bAPI\b/g, "KPI");
    
    t = t
      .replace(/\bCNC\b/gi, "씨앤씨")
      .replace(/\bMCT\b/gi, "엠씨티")
      .replace(/\bURL\b/gi, "유알엘")
      .replace(/\bKPI\b/gi, "케이피아이")
      .replace(/\bEA\b/g, "개")
      .replace(/소주/g, "수주")
      .replace(/우주/g, "수주")
      .replace(/우유/g, "수주")
      .replace(/EA/g, "개");

    t = t.replace(/^\s+$/gm, "");
    t = t.replace(/\n{3,}/g, "\n\n");
    t = t.replace(/\s+$/gm, "");

    return t;
  };

  const ttsFileName = (name) => {
    let s = String(name || "");
    s = s.replace(/[_\-]+/g, " ");
    s = s.replace(/\bDIFF\b/g, "디프");
    s = s.replace(/\.xlsx$/i, " 엑셀 파일");
    s = s.replace(/\.xls$/i, " 엑셀 파일");
    s = s.replace(/\.pdf$/i, " 피디에프 파일");
    s = s.replace(/\.png$/i, " 이미지 파일");
    s = s.replace(/\.jpg$/i, " 이미지 파일");
    s = s.replace(/\.jpeg$/i, " 이미지 파일");
    s = s.replace(/\.csv$/i, " 씨에스브이 파일");
    s = s.replace(/\.+/g, " ");
    s = s.replace(/\s+/g, " ").trim();
    return s;
  };

  const ttsFileSize = (sizeDp) => {
    const t = String(sizeDp || "").trim();
    if (!t) return "";
    return t
      .replace(/KB/i, "킬로바이트")
      .replace(/MB/i, "메가바이트")
      .replace(/GB/i, "기가바이트");
  };

  const ttsNormalize = (s) => {
    let t = normalizeSemantic(s);

    t = t
      .replace(/\bMES\b/g, "엠이에스")
      .replace(/\bCNC\b/g, "씨앤씨")
      .replace(/\bMCT\b/g, "엠씨티")
      .replace(/\bERP\b/g, "이알피")
      .replace(/\bKPI\b/g, "케이피아이")
      .replace(/\bAPI\b/g, "에이피아이");

    t = t.replace(/%/g, "퍼센트");
    t = t.replace(/\$/g, "달러");
    t = t.replace(/₩/g, "원");         
    t = t.replace(/℃/g, "도씨");        
    t = t.replace(/°C/gi, "도씨");      
    t = t.replace(/°/g, "도");          
    t = t.replace(/@/g, "골뱅이");       
    t = t.replace(/\+/g, "플러스");      
    t = t.replace(/=/g, "는");          
    t = t.replace(/&/g, "앤드");        
    t = t.replace(/#/g, "샵");          
    t = t.replace(/~/g, "에서");        

    t = t.replace(/(\d+)\s*EA\b/g, (_, n) => `${n}개`);
    t = t.replace(/\bEA\b/g, "개");

    t = t.replace(
      /(\d{4})-(\d{2})-(\d{2})\s*~\s*(\d{4})-(\d{2})-(\d{2})/g,
      (_, y1, m1, d1, y2, m2, d2) =>
        `${fmtKoreanDate(y1, m1, d1)} 에서 ${fmtKoreanDate(y2, m2, d2)}`
    );
    t = t.replace(/(\d{4})-(\d{2})-(\d{2})/g, (_, y, m, d) =>
      fmtKoreanDate(y, m, d)
    );

    t = t.replace(/^\s*-\s*파일\s*$/gim, "");
    t = t.replace(
      /^\s*-\s*([^\n]+?)\s+(\d+(?:\.\d+)?\s*(?:KB|MB|GB))\s*$/gim,
      (_, fname, fsize) =>
        `파일: ${ttsFileName(fname)}. 용량: ${ttsFileSize(fsize)}.`
    );

    t = t.replace(/\[([^\]]+)\]/g, "$1");
    t = t.replace(/\*/g, "");
    t = t.replace(/^\s*-\s*파일\s*$/gim, ""); 
    t = t.replace(/:(?!\/\/)/g, ":\n");
    
    t = highlightSpecMultipliers(t);
    t = highlightUnitNumbers(t);

    t = t.replace(/(\d),(?=\d{3}\b)/g, "$1"); 

    const numberWithCommas = /(?<!\d)(\d{1,3}(?:,\d{3})*)(?:\.(\d+))?(?!\d)/g;
    t = t.replace(numberWithCommas, (matches) =>
      numberToKoreanWords(matches)
    );

    t = t.replace(
      /\b([A-Z])(\d{6,})(?:-(\d{1,}))?\b/g,
      (_, pfx, main, lot) => {
        const key = String(pfx || "").toUpperCase();
        const info = CODE_PREFIX[key];
        if (!info) return `${key}${main}${lot ? "-" + lot : ""}`;

        const mainSpeak = digitsToKorean(main);
        if (lot) {
          const lotSpeak = digitsToKorean(lot);
          return `${info.label} ${mainSpeak} 다시 ${lotSpeak}`;
        }
        return `${info.label} ${mainSpeak}`;
      }
    );

    t = t.replace(/[^a-zA-Z0-9ㄱ-ㅎ가-힣\s.,!?]/g, " ");

    t = t.replace(/^\s+$/gm, "");      
    t = t.replace(/\n{3,}/g, "\n\n");  
    t = t.replace(/\s+$/gm, "");       
    t = t.replace(/ +/g, " ");         

    return t;
  };

  const hasTTS = hasWebTTS;

  const getVoicesSafe = () => {
    if (!hasTTS) return [];
    try {
      return window.speechSynthesis.getVoices() || [];
    } catch {
      return [];
    }
  };

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!window.speechSynthesis) return;

    const warm = () => {
      try {
        window.speechSynthesis.getVoices();
      } catch (e) {}
    };

    warm();

    const onChanged = () => warm();
    window.speechSynthesis.addEventListener?.("voiceschanged", onChanged);

    return () => {
      window.speechSynthesis.removeEventListener?.("voiceschanged", onChanged);
    };
  }, []);

  const saveVoiceUri = (v) => {
    try {
      if (typeof window === "undefined") return;
      if (v?.voiceURI) localStorage.setItem("tts_voice_uri", v.voiceURI);
    } catch (e) {}
  };

  const pickBestKoreanVoice = () => {
    const voices = getVoicesSafe();
    if (!voices.length) return null;

    const savedUri =
      typeof window !== "undefined"
        ? localStorage.getItem("tts_voice_uri")
        : null;

    if (savedUri) {
      const found = voices.find((v) => v.voiceURI === savedUri);
      if (found) return found;
      try {
        localStorage.removeItem("tts_voice_uri");
      } catch (e) {}
    }

    const ko = voices.filter((v) =>
      String(v.lang || "").toLowerCase().startsWith("ko")
    );

    const femaleHint =
      /(female|woman|여|여성|yuna|jini|seoyeon|sunhi|sora|mina|지니|유나|서연|선희|소라|미나)/i;

    const v =
      ko.find((x) => femaleHint.test(x.name) || femaleHint.test(x.voiceURI)) ||
      ko.find((x) => x.default) ||
      (ko.length ? ko[0] : null) ||
      voices.find((x) => x.default) ||
      voices[0];

    if (v) saveVoiceUri(v);
    return v || null;
  };

  const splitForTTS = (text) => {
    const t = String(text || "").trim();
    if (!t) return [];

    const withPauses = t.replace(
      /(\s*)(그리고|또한|다만|그래서|그러면|하지만)\s*:/g,
      "\n$2:"
    );

    const lines = withPauses
      .split(/\n+/)
      .map((x) => x.trim())
      .filter(Boolean);

    const out = [];
    for (const line of lines) {
      if (out.length > 0) out.push({ __gap: "line" });

      const parts = line
        .replace(/\s+/g, " ")
        .split(/(?<=[\.\!\?\。\！\？])\s+/g)
        .map((x) => x.trim())
        .filter(Boolean);

      for (const p of parts) {
        if (p.length <= 220) out.push(p);
        else out.push(p);
      }
    }
    return out;
  };

  const TTS_RATE = 1.1;
  const TTS_PITCH = 1.05;
  const TTS_GAP_MS = 100;
  const TTS_LINE_GAP_MS = 350;

  const stopTTS = () => {
    if (isAndroidWebView()) {
      androidCall("stopTTS");
      setIsSpeaking(false);
      isSpeakingRef.current = false;
      setSpeakingIdx(null);
      return;
    }

    if (!hasTTS) return;
    try {
      window.speechSynthesis.cancel();
    } catch (e) {}
    utterRef.current = null;

    ttsQueueRef.current = [];
    ttsQueueIdxRef.current = 0;

    setIsSpeaking(false);
    setSpeakingIdx(null);
  };

  const buildTtsForReply = (replyText, attachments) => {
    let t = String(replyText || "");
    t = t.replace(/^\s+$/gm, "");
    t = t.replace(/\n{3,}/g, "\n\n");
    t = t.replace(/\s+$/gm, "");
    return t;
  };

  const speakText = (text, idx = null) => {
    const raw = String(text || "").trim();
    if (!raw) return;

    if (isListening) {
      try {
        autoListenRef.current = false;
        recognitionRef.current?.abort?.();
      } catch (e) {}
      if (isAndroidWebView()) {
        try {
          androidCall("stopSTT");
        } catch (e) {}
      }
      setIsListening(false);
    }

    if (isAndroidWebView()) {
      stopTTS();
      setSpeakingIdx(idx);
      androidCall("speak", ttsNormalize(raw));
      return;
    }

    if (!hasTTS) {
      Swal.fire({
        title: '<p style="color:red">TTS 미지원</p>',
        text: "현재 브라우저에서 음성 출력(TTS)을 지원하지 않습니다.",
        confirmButtonText: "확인",
      });
      return;
    }

    const t = ttsNormalize(raw).trim();
    if (!t) return;

    stopTTS();

    try {
      try {
        window.speechSynthesis.getVoices();
      } catch (e) {}

      const voice = pickBestKoreanVoice();
      const chunks = splitForTTS(t);
      if (!chunks.length) return;

      ttsQueueRef.current = chunks;
      ttsQueueIdxRef.current = 0;

      const speakNext = () => {
        const list = ttsQueueRef.current || [];
        const i = ttsQueueIdxRef.current || 0;

        if (!list[i]) {
          setIsSpeaking(false);
          setSpeakingIdx(null);
          utterRef.current = null;
          ttsQueueRef.current = [];
          ttsQueueIdxRef.current = 0;
          return;
        }

        const item = list[i];

        if (item && typeof item === "object" && item.__gap === "line") {
          ttsQueueIdxRef.current = i + 1;
          setTimeout(speakNext, TTS_LINE_GAP_MS);
          return;
        }

        const u = new window.SpeechSynthesisUtterance(String(item));
        if (voice) u.voice = voice;
        u.lang = voice?.lang || "ko-KR";
        u.rate = TTS_RATE;
        u.pitch = TTS_PITCH;
        u.volume = 1.0;

        u.onstart = () => {
          setIsSpeaking(true);
          setSpeakingIdx(idx);
        };

        u.onend = () => {
          utterRef.current = null;
          ttsQueueIdxRef.current = i + 1;
          setTimeout(speakNext, TTS_GAP_MS);
        };

        u.onerror = () => {
          setIsSpeaking(false);
          setSpeakingIdx(null);
          utterRef.current = null;
          ttsQueueRef.current = [];
          ttsQueueIdxRef.current = 0;
        };

        utterRef.current = u;

        try {
          window.speechSynthesis.resume();
        } catch (e) {}

        window.speechSynthesis.speak(u);
      };

      setTimeout(() => {
        try {
          window.speechSynthesis.resume();
        } catch (e) {}
        speakNext();
      }, 80);
    } catch (e) {
      setIsSpeaking(false);
      setSpeakingIdx(null);
      utterRef.current = null;
      ttsQueueRef.current = [];
      ttsQueueIdxRef.current = 0;
    }
  };

  useEffect(() => {
    return () => {
      stopTTS();
      if (sttTimerRef.current) clearTimeout(sttTimerRef.current);
      if (sttSilenceRef.current) clearTimeout(sttSilenceRef.current);
      if (pendingAutoSendTimerRef.current)
        clearTimeout(pendingAutoSendTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetSttSilenceTimer = () => {
    if (!sttHasSpeechRef.current) return;
    if (sttSilenceRef.current) clearTimeout(sttSilenceRef.current);
    sttSilenceRef.current = setTimeout(() => {
      sttAutoSendRef.current = true;
      stopSTT();
    }, STT_SILENCE_MS);
  };

  const clearSttTimers = () => {
    if (sttTimerRef.current) clearTimeout(sttTimerRef.current);
    if (sttSilenceRef.current) clearTimeout(sttSilenceRef.current);
    sttTimerRef.current = null;
    sttSilenceRef.current = null;
  };

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!enableMic) return;
    if (isAndroidWebView()) return;

    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;

    const rec = new SR();
    rec.lang = "ko-KR";
    rec.continuous = true;
    rec.interimResults = true;
    rec.maxAlternatives = 1;

    rec.onresult = (event) => {
      let interim = "";
      let anyText = false;

      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const r = event.results[i];
        const t = (r?.[0]?.transcript || "").trim();
        if (t) anyText = true;
        if (r.isFinal) sttFinalRef.current += t;
        else interim += t;
      }

      if (anyText) {
        sttHasSpeechRef.current = true;
        resetSttSilenceTimer();
      }

      const merged = (sttFinalRef.current + interim).replace(/\s+/g, " ");
      setSttDraft(merged);
    };

    rec.onerror = () => {
      clearSttTimers();
      sttTimerRef.current = setTimeout(() => {
        setIsListening(false);

        const finalTextRaw = (sttFinalRef.current || "").trim();
        const draftTextRaw = (sttDraftRef.current || "").trim();
        const finalText = finalTextRaw || draftTextRaw;

        const base = (sttBaseInputRef.current || "").trim();
        const combined = [base, finalText].filter(Boolean).join(" ");

        setInput(combined);
        setSttDraft("");

        sttAutoSendRef.current = false;
        sttHasSpeechRef.current = false;

        const shouldRestart =
          autoListenRef.current &&
          !locked &&
          !isLoading &&
          !isSpeaking &&
          String(inputStateRef.current || "").trim() === "";

        if (shouldRestart) {
          setTimeout(() => {
            try {
              rec.start();
              setIsListening(true);
            } catch (e) {}
          }, 300);
        }
      }, 200);
    };

    rec.onend = () => {
      clearSttTimers();
      sttTimerRef.current = setTimeout(() => {
        setIsListening(false);

        const finalTextRaw = (sttFinalRef.current || "").trim();
        const draftTextRaw = (sttDraftRef.current || "").trim();
        const finalText = finalTextRaw || draftTextRaw;

        const base = (sttBaseInputRef.current || "").trim();
        const combined = [base, finalText].filter(Boolean).join(" ");

        setInput(combined);
        setSttDraft("");

        if (sttAutoSendRef.current && finalText) {
          sttAutoSendRef.current = false;
          pendingAutoSendTextRef.current = combined;
          if (pendingAutoSendTimerRef.current)
            clearTimeout(pendingAutoSendTimerRef.current);

          pendingAutoSendTimerRef.current = setTimeout(() => {
            const cur = String(inputStateRef.current || "").trim();
            const target = String(pendingAutoSendTextRef.current || "").trim();
            if (cur && target && cur === target && !isLoading && !locked) {
              handleSendText(cur, true);
            }
          }, STT_SEND_DELAY_MS);
        } else {
          sttAutoSendRef.current = false;
        }

        sttHasSpeechRef.current = false;

        const shouldRestart =
          autoListenRef.current &&
          !locked &&
          !isLoading &&
          !isSpeaking &&
          String(inputStateRef.current || "").trim() === "";

        if (shouldRestart) {
          setTimeout(() => {
            try {
              rec.start();
              setIsListening(true);
            } catch (e) {}
          }, 300);
        }
      }, 200);
    };

    recognitionRef.current = rec;

    return () => {
      try {
        autoListenRef.current = false;
        rec.abort();
      } catch (e) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableMic]);

  const formatDateTime = (ts) => {
    if (!ts) return "";
    try {
      const d = new Date(ts);
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      const hh = String(d.getHours()).padStart(2, "0");
      const mi = String(d.getMinutes()).padStart(2, "0");
      const ss = String(d.getSeconds()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`;
    } catch {
      return "";
    }
  };

  const buildHistoryForServer = (msgs) =>
    msgs
      .filter((m) => m.role === "user" || m.role === "assistant")
      .slice(-HISTORY_MAX)
      .map((m) => ({ role: m.role, content: m.content }));

  const normalizeServerPath = (p) => {
    if (!p) return "";
    let s = String(p);
    s = s.replaceAll("\\\\", "\\");
    s = s.replaceAll("/", "\\");
    s = s.replace(/^\\+/, "");
    s = s.replaceAll("..", "");
    return s;
  };

  const extractFileName = (fileName, filePath) => {
    if (fileName && String(fileName).trim()) return String(fileName).trim();
    const p = String(filePath || "");
    const last = p.split(/[/\\]/).pop();
    return last || "download";
  };

  const uploadOrderExcelFile = async (file) => {
    if (!file) throw new Error("파일이 없습니다.");

    const name = (file.name || "").toLowerCase();
    if (!name.endsWith(".xlsx") && !name.endsWith(".xlsm")) {
      throw new Error("템플릿 Excel 파일만 업로드 가능합니다.");
    }

    const form = new FormData();
    form.append("file", file); // ⭐ 서버 @RequestParam("file") 고정

    const res = await axios.post("/api/p/ExcelUpload/file", form, {
      withCredentials: true,
    });

    return res.data;
  };

  // ✅ 첨부 버튼(플러스 아이콘) 클릭 시, 파일선택 대신 메뉴 팝업 열기
  const handleAttachClick = (e) => {
    if (isMobileChat) return;
    if (locked || isLoading || isListening || isSpeaking || isUploadingFile) return;
    setAttachMenuAnchor(e.currentTarget);
  };

  // ✅ [추가] 첨부 메뉴 닫기 함수
  const closeAttachMenu = () => {
    setAttachMenuAnchor(null);
  };

  // ✅ [추가] 메뉴 항목: "수주템플릿 다운로드"
  const handleMenuTemplateDownload = () => {
    closeAttachMenu();
    
    try {
      // 💡 파일이 위치한 프론트엔드 경로 (public 폴더 기준)
      // 예: public/Files/수주정보.xlsx 에 넣었다면 아래와 같이 작성
      const fileUrl = '/Excel.zip'; 
      
      const link = document.createElement('a');
      link.href = fileUrl;
      link.setAttribute('download', '엑셀 템플릿.zip'); // 다운로드될 때의 파일명
      document.body.appendChild(link);
      link.click();
      link.remove();
      
    } catch (err) {
      console.error("템플릿 다운로드 에러:", err);
      Swal.fire({ 
        title: '<p style="color:red">다운로드 실패</p>', 
        text: '템플릿을 찾을 수 없거나 다운로드에 실패했습니다.', 
        confirmButtonText: '확인' 
      });
    }
  };

  // ✅ [추가] 메뉴 항목: "엑셀 업로드"
  const handleMenuUploadClick = () => {
    closeAttachMenu();
    fileInputRef.current?.click();
  };

  const processExcelUpload = async (file, source) => {
    if (!file) return;
    if (locked || isLoading || isListening || isSpeaking || isUploadingFile) return;

    if (appStore.loading) return;
    appStore.setLoading(true);

    try {
      setIsUploadingFile(true);
      const data = await uploadOrderExcelFile(file);
      
      // 백엔드가 주는 data.type 사용
      const uploadType = data?.type ? String(data.type).toLowerCase() : "";
      const payload = { from: source, fileName: file?.name || "", uploadResult: data };
      
      switch (uploadType) {
        case "order":
          //Swal.fire({ title: "업로드 완료", text: "수주 엑셀 업로드가 완료되었습니다.", confirmButtonText: "확인" });
          openOrderReg(payload);
          break;
        case "purchase":
          //Swal.fire({ title: "업로드 완료", text: "발주 엑셀 업로드가 완료되었습니다.", confirmButtonText: "확인" });
          openPurchaseReg(payload);
          break;
        case "incoming":
          //Swal.fire({ title: "업로드 완료", text: "입고 엑셀 업로드가 완료되었습니다.", confirmButtonText: "확인" });
          openIncomingReg(payload);
          break;
        case "outgoing":
          //Swal.fire({ title: "업로드 완료", text: "출하 엑셀 업로드가 완료되었습니다.", confirmButtonText: "확인" });
          openOutgoingReg(payload);
          break;
        default:
          throw new Error("MES에 등록되지 않은 엑셀 템플릿입니다.");
      }
      
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data || err?.message || "업로드 실패";

      Swal.fire({
        title: '<p style="color:red">엑셀 양식 오류</p>',
        html: `업로드하신 엑셀 파일의 양식이 맞지 않거나 데이터를 읽을 수 없습니다.<br/><br/><span style="color:gray; font-size:13px;">(상세오류: ${String(msg)})</span><br/><br/><b>표준 엑셀 템플릿을 다운로드 하시겠습니까?</b>`,
        showCancelButton: true,
        confirmButtonColor: '#10b981',
        cancelButtonColor: '#6b7280',
        confirmButtonText: '예, 양식 다운로드',
        cancelButtonText: '닫기'
      }).then((result) => {
        if (result.isConfirmed) handleMenuTemplateDownload();
      });
    } finally {
      setIsUploadingFile(false);
      appStore.setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // 파일 선택창 초기화
    if (file) {
      processExcelUpload(file, 'excel_upload');
    }
  };

  const tryUploadFromDragEvent = (evt) => {
    const dt = evt?.dataTransfer;
    const file = dt?.files?.[0];
    if (file) {
      processExcelUpload(file, 'excel_dragdrop');
    }
  };

  const startSTT = () => {
    if (!enableMic) return;
    if (locked || isLoading) return;
    if (isSpeaking) return;

    autoListenRef.current = false;

    sttUsedRef.current = true;
    sttAutoSendRef.current = false;
    sttHasSpeechRef.current = false;

    sttBaseInputRef.current = inputStateRef.current || "";

    setSttDraft("");
    sttFinalRef.current = "";
    setIsListening(true);

    if (isAndroidWebView()) {
      const ok = androidCall("startSTT");
      if (!ok) {
        setIsListening(false);
        Swal.fire({
          title: '<p style="color:red">STT 실패</p>',
          text: "Android STT 브릿지 호출에 실패했습니다.",
          confirmButtonText: "확인",
        });
      }
      return;
    }

    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = recognitionRef.current;

    if (!SR || !rec) {
      Swal.fire({
        title: '<p style="color:red">STT 미지원</p>',
        text:
          "현재 환경(WebView/브라우저)에서 웹 STT(Web Speech API)를 지원하지 않습니다.\n" +
          "※ 이 경우 안드로이드 앱 네이티브 STT로 브릿지해야 합니다.",
        confirmButtonText: "확인",
      });
      setIsListening(false);
      return;
    }

    try {
      rec.start();
    } catch (e) {
      setIsListening(false);
    }
  };

  const stopSTT = () => {
    if (!enableMic) return;

    autoListenRef.current = false;

    if (isAndroidWebView()) {
      androidCall("stopSTT");
      setIsListening(false);
      return;
    }

    const rec = recognitionRef.current;
    if (!rec) return;

    try {
      rec.stop();
    } catch (e) {}

    setTimeout(() => {
      try {
        rec.abort();
      } catch (e) {}
    }, 700);
  };

  const toggleSTT = () => {
    if (!enableMic) return;

    if (isListening) {
      sttAutoSendRef.current = false;
      stopSTT();
    } else startSTT();
  };

  const downloadFile = async (
    attachment,
    {
      url = "/api/SaveCommon/FileDownload",
      fileNameKey = "file_name",
      serverPathKey = "file_name_sever",
      serverParamName = "fileNameSever",
      fileParamName = "fileName",
    } = {}
  ) => {
    if (!attachment) return;

    const rawName = attachment?.[fileNameKey];
    const rawServer = attachment?.[serverPathKey];

    const finalName = extractFileName(rawName, rawServer);
    const serverPath = normalizeServerPath(rawServer);

    if (!serverPath) {
      console.log("다운로드 파라미터 부족:", attachment);
      return;
    }

    try {
      const response = await axios.post(
        url,
        new URLSearchParams({
          [fileParamName]: finalName,
          [serverParamName]: serverPath,
        }),
        { withCredentials: true, responseType: "blob" }
      );

      if (response.status === 200) {
        const blob = new Blob([response.data]);
        const dlUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = dlUrl;
        link.setAttribute("download", finalName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(dlUrl);
      }
    } catch (error) {
      console.log("네트워크 실패", error);
    }
  };

  const handleSendText = async (rawText, fromMic = false) => {
    cancelPendingAutoSend();
    const text = String(rawText || "").trim();
    if (!text) return;
    if (isLoading) return;
    if (locked) return;
    if (isListening) return;

    if (askCount >= DAILY_LIMIT) {
      setLocked(true);
      return;
    }

    lastSendByMicRef.current = !!fromMic || !!sttUsedRef.current;
    sttUsedRef.current = false;

    const now = Date.now();

    const nextMessages = [
      ...messages,
      { role: "user", content: text, ts: now, attachments: [] },
    ];
    setMessages(nextMessages);

    setInput("");
    setIsLoading(true);

    const nextAskCount = askCount + 1;
    setAskCount(nextAskCount);

    try {
      const res = await axios.post(
        API_URL,
        {
          message: text,
          page_url: window.location.href,
          company_id: Number(localStorage.getItem("company_id") ?? "0"),
          history: buildHistoryForServer(nextMessages),
          sticky_gpt_remaining: stickyLeft,
          last_sql_key: lastSqlKey,
          last_tool_params: lastToolParams,
          last_tool_rows_preview: lastToolRowsPreview,
        },
        { timeout: 60000 }
      );

      const botReply = res.data?.response ?? "응답을 받지 못했습니다.";
      const sticky = res.data?.sticky_gpt_remaining ?? 0;

      const attachments = Array.isArray(res.data?.attachments)
        ? res.data.attachments
        : [];

      setStickyLeft(sticky);

      setLastSqlKey(res.data?.last_sql_key ?? null);
      setLastToolParams(res.data?.last_tool_params ?? {});
      setLastToolRowsPreview(res.data?.last_tool_rows_preview ?? []);

      const afterBot = [
        ...nextMessages,
        {
          role: "assistant",
          content: botReply,
          ts: Date.now(),
          attachments,
        },
      ];

      if (nextAskCount >= DAILY_LIMIT) {
        setLocked(true);
      }

      setMessages(afterBot);

      if (lastSendByMicRef.current) {
        lastSendByMicRef.current = false;
        const ttsText = buildTtsForReply(botReply, attachments);
        speakText(ttsText, -1);
      }
    } catch (err) {
      console.error(err);

      const errMsgs = [
        ...nextMessages,
        {
          role: "error",
          content: "서버 호출 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.",
          ts: Date.now(),
          attachments: [],
        },
      ];

      if (nextAskCount >= DAILY_LIMIT) {
        setLocked(true);
      }

      setMessages(errMsgs);
    } finally {
      setIsLoading(false);
    }
  };
  handleSendTextRef.current = handleSendText;

  const handleSend = async () => {
    const text = input.trim();
    return handleSendText(text, false);
  };

  const handleKeyDown = (e) => {
    if (locked || isLoading || isListening) {
      e.preventDefault();
      return;
    }

    if (e.key === "Enter") {
      if (!e.shiftKey && !e.nativeEvent.isComposing) {
        e.preventDefault();
        handleSend();
      }
    }
  };

  const hasText = input.trim().length > 0;
  const sendButtonStyles = {
    width: 40,
    height: 40,
    borderRadius: 999,
    ...(hasText
      ? {
          backgroundColor: "#3b82f6",
          color: "#fff",
          "&:hover": { backgroundColor: "#2563eb" },
        }
      : {}),
  };

  const handleRightAction = () => {
    if (isSpeaking) {
      stopTTS();
      return;
    }

    if (hasText) {
      handleSend();
      return;
    }

    if (enableMic) {
      toggleSTT();
    } else {
      handleAttachClick();
    }
  };

  const chatBody = (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          width: "100%",
          gap: 0,
          paddingTop: 4,
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        {locked && (
          <div
            style={{
              marginBottom: 2,
              padding: "10px 12px",
              borderRadius: 10,
              backgroundColor: "#fee2e2",
              border: "1px solid #ef4444",
              color: "#991b1b",
              fontSize: 13,
              fontWeight: 700,
              flex: "0 0 auto",
            }}
          >
            더 이상 질문할 수 없습니다. (오늘 제한 {DAILY_LIMIT}회)
          </div>
        )}

        {/* 대화 영역 */}
        <div
          ref={listRef}
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overscrollBehavior: "contain",
            WebkitOverflowScrolling: "touch",
            border: "1px solid #ddd",
            borderRadius: 10,
            padding: "5px 12px",
            backgroundColor: "#fafafa",
          }}
        >
          {messages.map((m, idx) => {
            const isUser = m.role === "user";
            const isError = m.role === "error";
            const isAssistant = m.role === "assistant";

            const bubbleBg = isError ? "#ef4444" : isUser ? "#1976d2" : "white";
            const bubbleColor = isError || isUser ? "white" : "black";
            const bubbleBorder = isError ? "1px solid #ef4444" : "1px solid #ddd";

            const atts = Array.isArray(m.attachments) ? m.attachments : [];
            const orderFiles = atts.filter((a) => a?.type === "order_file");

            const canSpeakThis =
              isAssistant && !isUser && !isError && String(m.content || "").trim();

            const isThisSpeaking = isSpeaking && speakingIdx === idx;

            return (
              <div
                key={idx}
                style={{
                  marginBottom: 10,
                  textAlign: isUser ? "right" : "left",
                }}
              >
                <div
                  style={{
                    display: "inline-block",
                    padding: "8px 12px",
                    borderRadius: 10,
                    backgroundColor: bubbleBg,
                    color: bubbleColor,
                    border: bubbleBorder,
                    maxWidth: "90%",
                    whiteSpace: "pre-wrap",
                    textAlign: "left",
                    fontSize: 14,
                    lineHeight: 1.45,
                  }}
                >
                  {m.content}
                </div>

                {!isUser && !isError && orderFiles.length > 0 && (
                  <div
                    style={{
                      marginTop: 8,
                      display: "flex",
                      flexDirection: "column",
                      gap: 4,
                    }}
                  >
                    {orderFiles.map((f, i) => {
                      const name = extractFileName(f.file_name, f.file_name_sever);

                      return (
                        <div
                          key={i}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            maxWidth: "90%",
                            color: "#374151",
                            fontSize: 13,
                          }}
                        >
                          <span
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={name}
                          >
                            {name}
                            {f.size_dp ? ` (${f.size_dp})` : ""}
                          </span>

                          <IconButton
                            size="small"
                            onClick={() => downloadFile(f)}
                            sx={{
                              padding: "2px",
                              border: "1px solid #e5e7eb",
                              borderRadius: "8px",
                              backgroundColor: "#fff",
                              "&:hover": { backgroundColor: "#f3f4f6" },
                            }}
                            title="다운로드"
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div
                  style={{
                    marginTop: 4,
                    fontSize: 11,
                    color: "#6b7280",
                    textAlign: isUser ? "right" : "left",
                    paddingLeft: isUser ? 0 : 2,
                    paddingRight: isUser ? 2 : 0,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    justifyContent: isUser ? "flex-end" : "flex-start",
                  }}
                >
                  <span>{formatDateTime(m.ts)}</span>

                  {canSpeakThis && (
                    <IconButton
                      size="small"
                      onClick={() => {
                        if (isThisSpeaking) stopTTS();
                        else speakText(m.content, idx);
                      }}
                      sx={{
                        padding: "2px",
                        border: "1px solid #e5e7eb",
                        borderRadius: "8px",
                        backgroundColor: "#fff",
                        "&:hover": { backgroundColor: "#f3f4f6" },
                      }}
                      title={isThisSpeaking ? "읽기 중지" : "읽어주기"}
                    >
                      {isThisSpeaking ? (
                        <StopCircleIcon fontSize="small" />
                      ) : (
                        <VolumeUpIcon fontSize="small" />
                      )}
                    </IconButton>
                  )}
                </div>
              </div>
            );
          })}

          {isLoading && (
            <div style={{ display: "flex", justifyContent: "flex-start" }}>
              <div
                style={{
                  padding: "8px 12px",
                  borderRadius: 10,
                  backgroundColor: "#fafafa",
                  fontSize: 12,
                  border: "1px solid #ddd",
                }}
              >
                생각 중입니다...
              </div>
            </div>
          )}
        </div>

        {/* ✅ 입력 영역 */}
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            paddingTop: 6,
            paddingBottom: "max(env(safe-area-inset-bottom), 6px)",
            flex: "0 0 auto",
          }}
        >
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              background: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: 999,
              paddingLeft: 10,
              paddingRight: 10,
              minHeight: 52,
              boxSizing: "border-box",
            }}
          >
            {/* PC에서만 첨부 버튼 및 팝업 메뉴 */}
            {!isMobileChat && (
              <>
                <IconButton
                  aria-label="첨부"
                  onClick={handleAttachClick}
                  disabled={locked || isLoading || isListening || isSpeaking || isUploadingFile}
                  sx={{ width: 40, height: 40, borderRadius: 999 }}
                >
                  <span style={{ fontSize: 22, lineHeight: 1 }}>＋</span>
                </IconButton>
                
                {/* ✅ [추가] 첨부 메뉴 팝업 (템플릿 다운로드 / 엑셀 업로드) */}
                <Menu
                  anchorEl={attachMenuAnchor}
                  open={attachMenuOpen}
                  onClose={closeAttachMenu}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  PaperProps={{
                    elevation: 0,
                    sx: {
                      overflow: 'visible',
                      filter: 'drop-shadow(0px 4px 12px rgba(0,0,0,0.1))',
                      mt: -1, // 메뉴 뜨는 위치 살짝 위로 조정
                      borderRadius: '12px', // 둥근 모서리
                      border: '1px solid #e5e7eb',
                      minWidth: '180px',
                      padding: '4px 0'
                    },
                  }}
                >
                  {/* 다운로드 버튼 */}
                  <MenuItem 
                    onClick={handleMenuTemplateDownload}
                    sx={{
                      fontSize: '14px',
                      fontWeight: 700,
                      color: '#374151',
                      padding: '12px 18px',
                      transition: 'all 0.2s ease',
                      '&:hover': { backgroundColor: '#f0f9ff', color: '#2563eb' } // 마우스 올리면 파란색 톤
                    }}
                  >
                    <DownloadIcon sx={{ fontSize: 20, mr: 1.5, color: 'inherit' }} />
                    템플릿 다운로드
                  </MenuItem>

                  {/* 업로드 버튼 */}
                  <MenuItem 
                    onClick={handleMenuUploadClick}
                    sx={{
                      fontSize: '14px',
                      fontWeight: 700,
                      color: '#374151',
                      padding: '12px 18px',
                      transition: 'all 0.2s ease',
                      '&:hover': { backgroundColor: '#f0fdf4', color: '#10b981' } // 마우스 올리면 초록색 톤
                    }}
                  >
                    <AttachFileIcon sx={{ fontSize: 20, mr: 1.5, color: 'inherit' }} />
                    엑셀 파일 업로드
                  </MenuItem>
                </Menu>
              </>
            )}

            {!isMobileChat && (
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel.sheet.macroEnabled.12"
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
            )}

            <textarea
              ref={inputRef}
              placeholder={
                locked
                  ? "더 이상 질문할 수 없습니다."
                  : isListening
                  ? "듣는 중..."
                  : "무엇이든 물어보세요"
              }
              value={
                isListening && !isAndroidWebView()
                  ? [sttBaseInputRef.current, (sttDraft || "").trim() ? sttDraft : ""]
                      .filter(Boolean)
                      .join(" ")
                  : input
              }
              onChange={(e) => {
                if (!isListening) {
                  sttUsedRef.current = false;
                  setInput(e.target.value);
                }
              }}
              onKeyDown={handleKeyDown}
              disabled={locked || isLoading || isListening}
              rows={1}
              style={{
                flex: 1,
                resize: "none",
                border: "none",
                outline: "none",
                fontSize: 16,
                padding: "10px 8px",
                background: "transparent",
                lineHeight: "22px",
                maxHeight: 140,
              }}
            />

            {/* 모바일에서만 마이크 */}
            {enableMic && (
              <IconButton
                aria-label={isListening ? "정지" : isSpeaking ? "읽기중지" : "마이크"}
                onClick={() => {
                  if (isSpeaking) {
                    stopTTS();
                    return;
                  }
                  if (isListening) {
                    stopSTT();
                  } else {
                    startSTT();
                  }
                }}
                disabled={locked || isLoading}
                sx={{ width: 40, height: 40, borderRadius: 999 }}
              >
                {isSpeaking ? (
                  <StopIcon fontSize="small" />
                ) : isListening ? (
                  <StopIcon fontSize="small" />
                ) : (
                  <MicIcon fontSize="small" />
                )}
              </IconButton>
            )}

            <IconButton
              aria-label="전송"
              onClick={() => {
                if (hasText) handleSendText(input.trim(), false);
              }}
              disabled={!hasText || locked || isLoading || isListening}
              sx={sendButtonStyles}
            >
              <SendIcon fontSize="small" />
            </IconButton>
          </div>
        </div>
      </div>
    </div>
  );

  // ✅ Full 모드
  if (shouldFull) {
    return (
      <div
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          top: topOffset,
          height: `calc(var(--app-height, 100dvh) - ${topOffset}px)`,
          background: "#f9fafb",
          overflow: "hidden",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            width: "min(960px, 100%)",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            padding: "0 12px",
            boxSizing: "border-box",
            minHeight: 0,
            overflow: "hidden",
            margin: "0 auto",
          }}
        >
          <div
            style={{
              flex: "0 0 auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
              paddingTop: 5,
              paddingBottom: 6,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                fontWeight: 700,
                fontSize: 20,
                minWidth: 0,
              }}
            >
              <span
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {(localStorage.getItem("company_nm") ?? "")} MES AI 챗봇
              </span>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              {geoLabel && (
                <div style={geoLabelBoxStyle}>
                  <span style={geoLabelStyle}>{geoLabel}</span>
                  {geoFull && (
                    <Tooltip
                      title={geoFull}
                      arrow
                      open={geoTipOpen}
                      onClose={closeGeoTip}
                      disableFocusListener
                      disableHoverListener
                      disableTouchListener
                    >
                      <IconButton
                        size="small"
                        aria-label="주소 보기"
                        onClick={openGeoTip}
                        sx={locationIconStyle}
                      >
                        <span style={{ fontSize: 16, lineHeight: 1 }}>📍</span>
                      </IconButton>
                    </Tooltip>
                  )}
                </div>
              )}
              <IconButton
                size="small"
                aria-label="닫기"
                onClick={chatModalClose}
                sx={closeButtonStyle}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </div>
          </div>

          <div style={{ flex: "1 1 auto", minHeight: 0, overflow: "hidden" }}>
            {chatBody}
          </div>
          
          {/* ✅ 수주 등록 모달 (Full 화면 모드 시) */}
          {orderRegOpen && (
            <OrderRegModal
              title={"수주 등록"}
              onClose={closeOrderReg}
              userTypeModalOption={userTypeModalOption}
              unitCurrencyModalOption={unitCurrencyModalOption}
              payload={orderRegPayload}
              MenuUpdateYN={orderMenuUpdateYN}
              handleRegSuccessSave={handleRegSuccessSave} // ✅ 이 줄 추가!
            />
          )}
          {purchaseRegOpen && (
            <PurchaseRegModal
              title={"발주 등록"}
              onClose={closePurchaseReg}
              userTypeModalOption={userTypeModalOption}
              unitCurrencyModalOption={unitCurrencyModalOption}
              payload={purchaseRegPayload}
              MenuUpdateYN={purchaseMenuUpdateYN}
              handleRegSuccessSave={handlePurchaseRegSuccessSave}
            />
          )}

          {incomingRegOpen && (
            <IncomingRegModal
              title={"입고 등록"}
              onClose={closeIncomingReg}
              userTypeModalOption={userTypeModalOption}
              qualityYnModalOption={qualityYnModalOption} // 세팅한 검사여부 옵션 전달
              payload={incomingRegPayload} // 엑셀 데이터 전달!
              MenuUpdateYN={incomingMenuUpdateYN}
              handleRegSuccessSave={handleIncomingRegSuccessSave}
            />
          )}

          {outgoingRegOpen && (
            <OutgoingRegModal
              title={"출하 등록"}
              onClose={closeOutgoingReg}
              userTypeModalOption={userTypeModalOption}
              qualityYnModalOption={qualityYnModalOption} // 출하 모달도 받도록 설정되어 있다면 전달
              payload={outgoingRegPayload} // 엑셀 데이터 전달!
              MenuUpdateYN={outgoingMenuUpdateYN}
              handleRegSuccessSave={handleOutgoingRegSuccessSave}
            />
          )}
        </div>
      </div>
    );
  }

  // ✅ PC 모달
  return (
    <PCModal
      title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              width: "100%",
            }}
          >
          <div style={{ fontWeight: 700, fontSize: 18, minWidth: 0 }}>
            <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {(localStorage.getItem("company_nm") ?? "")} MES AI 챗봇
            </span>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              minWidth: 0,
              gap: 8,
            }}
          >
            <div style={geoLabelStyle}>{geoLabel ? geoLabel : ""}</div>
            {geoFull ? (
              <Tooltip
                title={geoFull}
                arrow
                open={geoTipOpen}
                onClose={closeGeoTip}
                disableFocusListener
                disableHoverListener
                disableTouchListener
              >
                <IconButton
                  size="small"
                  aria-label="주소"
                  onClick={openGeoTip}
                  sx={locationIconStyle}
                >
                  <span style={{ fontSize: 15, lineHeight: 1 }}>📍</span>
                </IconButton>
              </Tooltip>
            ) : null}
            <IconButton
              size="small"
              aria-label="닫기"
              onClick={chatModalClose}
              sx={closeButtonStyle}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
        </div>
      }
      onClose={chatModalClose}
    >
      <div
        onDragEnter={(e) => {
          if (isMobileChat) return;
          e.preventDefault();
          e.stopPropagation();
          dragCounterRef.current += 1;
          setIsDragOver(true);
        }}
        onDragOver={(e) => {
          if (isMobileChat) return;
          e.preventDefault();
          e.stopPropagation();
          if (!isDragOver) setIsDragOver(true);
        }}
        onDragLeave={(e) => {
          if (isMobileChat) return;
          e.preventDefault();
          e.stopPropagation();
          dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
          if (dragCounterRef.current === 0) setIsDragOver(false);
        }}
        onDrop={async (e) => {
          if (isMobileChat) return;
          e.preventDefault();
          e.stopPropagation();
          dragCounterRef.current = 0;
          setIsDragOver(false);
          await tryUploadFromDragEvent(e);
        }}
        style={{
          width: 560,
          height: "min(80vh, 720px)",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          position: "relative",
        }}
      >
        {!isMobileChat && isDragOver && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 50,
              background: "rgba(255,255,255,0.85)",
              border: "2px dashed #9ca3af",
              borderRadius: 12,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
              color: "#374151",
              fontWeight: 800,
              fontSize: 16,
            }}
          >
            엑셀 파일 업로드
          </div>
        )}
        <div style={{ flex: "1 1 auto", minHeight: 0, overflow: "hidden" }}>
          {chatBody}
        </div>
        
        {/* ✅ 수주 등록 모달 (PC 모달 창 모드 시) */}
        {orderRegOpen && (
          <OrderRegModal
            title={"수주 등록"}
            onClose={closeOrderReg}
            userTypeModalOption={userTypeModalOption}
            unitCurrencyModalOption={unitCurrencyModalOption}
            payload={orderRegPayload}
            MenuUpdateYN={orderMenuUpdateYN}
            handleRegSuccessSave={handleRegSuccessSave} 
          />
        )}
        {purchaseRegOpen && (
            <PurchaseRegModal
              title={"발주 등록"}
              onClose={closePurchaseReg}
              userTypeModalOption={userTypeModalOption}
              unitCurrencyModalOption={unitCurrencyModalOption}
              payload={purchaseRegPayload}
              MenuUpdateYN={purchaseMenuUpdateYN}
              handleRegSuccessSave={handlePurchaseRegSuccessSave}
            />
          )}

        {incomingRegOpen && (
          <IncomingRegModal
            title={"입고 등록"}
            onClose={closeIncomingReg}
            userTypeModalOption={userTypeModalOption}
            qualityYnModalOption={qualityYnModalOption} // 세팅한 검사여부 옵션 전달
            payload={incomingRegPayload} // 엑셀 데이터 전달!
            MenuUpdateYN={incomingMenuUpdateYN}
            handleRegSuccessSave={handleIncomingRegSuccessSave}
          />
        )}

        {outgoingRegOpen && (
          <OutgoingRegModal
            title={"출하 등록"}
            onClose={closeOutgoingReg}
            userTypeModalOption={userTypeModalOption}
            qualityYnModalOption={qualityYnModalOption} // 출하 모달도 받도록 설정되어 있다면 전달
            payload={outgoingRegPayload} // 엑셀 데이터 전달!
            MenuUpdateYN={outgoingMenuUpdateYN}
            handleRegSuccessSave={handleOutgoingRegSuccessSave}
          />
        )}
      </div>
    </PCModal>
  );
} 

export default ChatModal;